/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import { getResource } from "../../../language/resources/getResource";
import LabelGroup, { ROW_GROUP } from "../../../forms/elements/LabelGroup";
import DateInput from "../../../forms/elements/DateInput";
import { DARK_GREY } from "../../../styles/colors";
import { Column, Row } from "../../../layout/flexbox";
import { useEffect, useMemo, useState } from "react";
import {
  createEmptyEditableEffectiveDateRangeRule,
  EditableEffectiveDateRangeAccessRule,
} from "./effectiveDateRange";
import { TimeFormat } from "../../../forms/elements/timeInputUtils";
import { useDateRangeValidation } from "../validation/dateRange/useDateRangeValidation";
import {
  getHardSeasonEnd,
  getHardSeasonStart,
  getSoftSeasonEnd,
  getSoftSeasonStart,
} from "../validation/seasonDates/seasonDate.utils";
import { ValidationIndicator } from "../validation/ValidationIndicator";
import { DateRangeIssue } from "../validation/dateRange/dateRange.types";
import { validationStyles } from "../validation/validationStyles";
import { TimeRangeInput } from "../validation/timeRange/TimeRangeInput";
import { shouldShowSubmitValidationForRow } from "../validation/validationUtils";

const dashCss = css`
  width: 10px;
  height: 2px;
  background-color: ${DARK_GREY};
  margin: 0px 9px;
`;

export const Dash = () => <span css={dashCss} />;

const containerCss = css`
  display: flex;
`;

const flexCenter = css`
  display: flex;
  align-items: center;
`;

const inputBaseCss = css`
  width: 100%;
  box-sizing: border-box;
`;

const dateTimeCss = css`
  width: 9.4rem;
  flex: 0 0 9.4rem;
`;

function mapDateRangeIssueToMessage(
  issue: DateRangeIssue,
  resources: Record<string, any>,
): string {
  switch (issue.code) {
    case "START_AND_END_REQUIRED":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EFFECTIVE_START_AND_EXPIRATION_REQUIED",
      );

    case "START_REQUIRED":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EFFECTIVE_START_DATE_REQUIRED",
      );

    case "END_REQUIRED":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EFFECTIVE_END_DATE_REQUIRED",
      );

    case "START_AFTER_END":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EFFECTIVE_START_AFTER_END",
      );

    case "START_BEFORE_HARD_MIN":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EFFECTIVE_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_START",
      );

    case "START_AFTER_HARD_MAX":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EFFECTIVE_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_END",
      );

    case "END_AFTER_HARD_MAX":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EXPIRATION_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_END",
      );

    case "END_BEFORE_HARD_MIN":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EXPIRATION_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_START",
      );

    case "START_BEFORE_SOFT_MIN":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EFFECTIVE_DATE_OUTSIDE_SEASON_START",
      );

    case "END_AFTER_SOFT_MAX":
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_EXPIRATION_DATE_OUTSIDE_SEASON_END",
      );

    default:
      return getResource(
        resources,
        "ACCESS_RULE_EDIT_UNKNOWN_VALIDATION_ERROR",
      );
  }
}

function getDateInputBorderStyle(
  field: "start" | "end",
  issues: DateRangeIssue[],
): SerializedStyles | undefined {
  const fieldIssues = issues.filter((issue) => issue.field === field);

  if (fieldIssues.some((i) => i.severity === "error")) {
    return validationStyles.errorBorder;
  }

  if (fieldIssues.some((i) => i.severity === "warning")) {
    return validationStyles.warningBorder;
  }

  return undefined;
}

type Props = {
  resources: Record<string, any>;
  season: string;
  dateFormat: string;
  timeFormat: TimeFormat;
  rule?: EditableEffectiveDateRangeAccessRule;
  onChange: (rule: EditableEffectiveDateRangeAccessRule | undefined) => void;
  submitCount: number;
  hasChangesSinceSubmit: boolean;
  /** Which parts to render (bulk editor edits dates/time separately). */
  showDate?: boolean;
  showTime?: boolean;
};

/**
 * This row edits a required Effective Date Range rule.
 * Clearing values represents an invalid or incomplete state, not deletion.
 *
 * Editing model: local draft state with parent reconciliation.
 *
 * The row maintains local state for user interaction and commits
 * changes upward via onChange.
 *
 * The parent editor owns the source of truth and computes persistence
 * diffs (created / updated / deleted).
 *
 * Local draft state re-syncs only when the rule’s identity changes.
 */
export function EffectiveDateRangeRuleRow({
  resources,
  season,
  dateFormat,
  timeFormat,
  rule,
  onChange,
  submitCount,
  hasChangesSinceSubmit,
  showDate = true,
  showTime = true,
}: Props) {
  // Local state for effective date/time inputs
  const [effectiveDateStart, setEffectiveDateStart] = useState<Date | null>(
    rule?.effectiveDateStart ?? null,
  );

  const [effectiveDateEnd, setEffectiveDateEnd] = useState<Date | null>(
    rule?.effectiveDateEnd ?? null,
  );

  const [effectiveTimeSlotStart, setEffectiveTimeSlotStart] =
    useState<Date | null>(rule?.effectiveTimeSlotStart ?? null);

  const [effectiveTimeSlotEnd, setEffectiveTimeSlotEnd] = useState<Date | null>(
    rule?.effectiveTimeSlotEnd ?? null,
  );

  const effectiveDateRangeIsEmpty =
    rule?.effectiveDateStart === null && rule?.effectiveDateEnd === null;

  const showSubmitValidation = shouldShowSubmitValidationForRow(
    submitCount,
    hasChangesSinceSubmit,
    effectiveDateRangeIsEmpty,
  );

  useEffect(() => {
    // Trigger validation for untouched rows on save
  }, [submitCount, hasChangesSinceSubmit]);

  // Update local state when parent rules changes
  useEffect(() => {
    if (!rule) {
      setEffectiveDateStart(null);
      setEffectiveDateEnd(null);
      setEffectiveTimeSlotStart(null);
      setEffectiveTimeSlotEnd(null);
      return;
    }

    setEffectiveDateStart(rule.effectiveDateStart ?? null);
    setEffectiveDateEnd(rule.effectiveDateEnd ?? null);
    setEffectiveTimeSlotStart(rule.effectiveTimeSlotStart ?? null);
    setEffectiveTimeSlotEnd(rule.effectiveTimeSlotEnd ?? null);

    // Intentional - only reset local state when the specific rule changes, not on every prop change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.liftAccessRuleId]);

  // Validation
  const dateRangeConstraints = useMemo(() => {
    if (!season) return undefined;

    return {
      hardMinStart: getHardSeasonStart(season),
      hardMaxEnd: getHardSeasonEnd(season),
      softMinStart: getSoftSeasonStart(season),
      softMaxEnd: getSoftSeasonEnd(season),
    };
  }, [season]);

  const dateRangeValidation = useDateRangeValidation(
    effectiveDateStart,
    effectiveDateEnd,
    dateRangeConstraints,
  );

  const effectiveValidation = useMemo(() => {
    // Suppress validation ONLY if the row is empty AND
    // we are NOT showing submit-time validation
    if (effectiveDateRangeIsEmpty && !showSubmitValidation) {
      return {
        issues: [],
        errors: [],
        warnings: [],
        primary: null,
        hasErrors: false,
        hasWarnings: false,
      };
    }
    return dateRangeValidation;
  }, [dateRangeValidation, effectiveDateRangeIsEmpty, showSubmitValidation]);

  const primaryMessage = effectiveValidation.primary
    ? mapDateRangeIssueToMessage(effectiveValidation.primary, resources)
    : undefined;

  const startDateBorderCss = useMemo(
    () => getDateInputBorderStyle("start", effectiveValidation.issues),
    [effectiveValidation.issues],
  );

  const endDateBorderCss = useMemo(
    () => getDateInputBorderStyle("end", effectiveValidation.issues),
    [effectiveValidation.issues],
  );

  // Update parent rule on local state change (after initial mount)
  const handleChange = (
    patch: Partial<EditableEffectiveDateRangeAccessRule>,
  ) => {
    const base = rule ?? createEmptyEditableEffectiveDateRangeRule();
    onChange({ ...base, ...patch });
  };

  const handleEffectiveDateStartChange = (date: Date | null) => {
    setEffectiveDateStart(date ?? null);
    handleChange({ effectiveDateStart: date ?? null });
  };

  const handleEffectiveDateEndChange = (date: Date | null) => {
    setEffectiveDateEnd(date ?? null);
    handleChange({ effectiveDateEnd: date ?? null });
  };

  const handleEffectiveTimeSlotChange = ({
    start,
    end,
  }: {
    start: Date | null;
    end: Date | null;
  }) => {
    setEffectiveTimeSlotStart(start);
    setEffectiveTimeSlotEnd(end);
    handleChange({
      effectiveTimeSlotStart: start,
      effectiveTimeSlotEnd: end,
    });
  };

  const ruleIdTitle = rule?.liftAccessRuleId
    ? getResource(resources, "ACCESS_RULE_EDIT_RULE_ID", rule.liftAccessRuleId)
    : getResource(resources, "ACCESS_RULE_EDIT_NEW_RULE");

  return (
    <div css={containerCss}>
      <Column>
        {showDate && (
        <Row rootCss={flexCenter}>
          <LabelGroup
            label={getResource(
              resources,
              "ACCESS_RULE_EDIT_EFFECTIVE_DATE_RANGE",
            )}
            showRequired
            requiredTitle={getResource(resources, "ACCESS_RULE_EDIT_REQUIRED")}
            rootCss={ROW_GROUP}
          >
            <DateInput
              id="effective-date-start-input"
              date={effectiveDateStart ?? undefined}
              onChange={handleEffectiveDateStartChange}
              placeholderText={getResource(
                resources,
                "ACCESS_RULE_EDIT_DATE_START",
              )}
              tooltipText={ruleIdTitle}
              dateFormat={dateFormat}
              rootCss={css`
                ${dateTimeCss};
                margin-left: 11px;
                ${startDateBorderCss};
              `}
            />
          </LabelGroup>
          <Dash />
          <DateInput
            id="effective-date-end-input"
            date={effectiveDateEnd ?? undefined}
            onChange={handleEffectiveDateEndChange}
            placeholderText={getResource(
              resources,
              "ACCESS_RULE_EDIT_DATE_EXPIRE",
            )}
            tooltipText={ruleIdTitle}
            dateFormat={dateFormat}
            rootCss={css`
              ${dateTimeCss};
              ${endDateBorderCss};
            `}
          />
          {
            <ValidationIndicator
              severity={effectiveValidation?.primary?.severity}
              message={primaryMessage}
              visible={!!effectiveValidation.primary}
            />
          }
        </Row>
        )}
        {showTime && (
        <Row
          rootCss={css`
            margin-top: ${showDate ? "20px" : "0"};
            ${flexCenter}
          `}
        >
          <LabelGroup
            label={getResource(
              resources,
              "ACCESS_RULE_EDIT_EFFECTIVE_TIME_RANGE",
            )}
            showRequired
            requiredTitle={getResource(resources, "ACCESS_RULE_EDIT_REQUIRED")}
            rootCss={ROW_GROUP}
          >
            <TimeRangeInput
              resources={resources}
              start={effectiveTimeSlotStart}
              startPlaceholder={getResource(
                resources,
                "ACCESS_RULE_EDIT_TIME_START",
              )}
              end={effectiveTimeSlotEnd}
              endPlaceholder={getResource(
                resources,
                "ACCESS_RULE_EDIT_TIME_END",
              )}
              title={ruleIdTitle}
              format={timeFormat}
              onChange={handleEffectiveTimeSlotChange}
              showDash
              timeInputCss={css`
                ${dateTimeCss};
                ${inputBaseCss}
              `}
              rootCss={css`
                margin-left: 9px;
              `}
              forceValidationVersion={submitCount}
              clearValidation={hasChangesSinceSubmit}
            />
          </LabelGroup>
        </Row>
        )}
      </Column>
    </div>
  );
}
